/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { FancyScrollStyles, styles } from "@eisinc/App/Components/FancyScroll/FancyScroll.style";
import ChevronDown from "@eisinc/XDL/Icons/ChevronDown";
import ChevronUp from "@eisinc/XDL/Icons/ChevronUp";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon from "@insite/mobius/Icon";
import React, { FC, PropsWithChildren, useCallback, useRef, useState } from "react";

interface FancyScrollProps {
    showOverlay?: boolean;
    bottomOnly?: boolean;
    extendStyle?: FancyScrollStyles;
    clickable?: boolean;
}

const FancyScroll: FC<PropsWithChildren<FancyScrollProps>> = ({
    children,
    showOverlay,
    extendStyle,
    bottomOnly,
    clickable,
}) => {
    const mergedStyles = mergeToNew(styles, extendStyle);
    const [scrollAbelContainerRef, setRef] = useState<any>();
    const elementEnd: any = useRef(null);
    const elementStart: any = useRef(null);

    const onScrollAbelContainerRefChange = useCallback((node: any) => {
        setRef(node);

        if (node && node.parentElement?.style && node.parentElement?.style.position !== "relative") {
            node.parentElement.style.position = "relative";

            if (node.scrollHeight > node.offsetHeight) {
                setScrollDownVisible(true);
            }
        }
    }, []);

    const [isScrollUpVisible, setScrollUpVisible] = React.useState(false);
    const [isScrollDownVisible, setScrollDownVisible] = React.useState(false);

    const updateScrollingControlsVisibilityState = () => {
        if (scrollAbelContainerRef) {
            if (!bottomOnly) {
                if (scrollAbelContainerRef.scrollTop <= 1) {
                    setScrollUpVisible(false);
                } else {
                    setScrollUpVisible(true);
                }
            }

            if (
                Math.ceil(scrollAbelContainerRef.scrollTop) >=
                Math.ceil(scrollAbelContainerRef.scrollHeight - scrollAbelContainerRef.clientHeight) - 1
            ) {
                setScrollDownVisible(false);
            } else {
                setScrollDownVisible(true);
            }
        }
    };

    const handleScroll = (element: any) => {
        element?.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
    };

    const handleClick = (e: any, direction: string) => {
        e.preventDefault();
        e.stopPropagation();

        if (!clickable) {
            return;
        }

        if (direction === "DOWN") {
            handleScroll(elementEnd);

            return;
        }

        handleScroll(elementStart);
    };

    return (
        <>
            <StyledWrapper
                css={mergedStyles.outerContainer}
                onScroll={() => updateScrollingControlsVisibilityState()}
                ref={onScrollAbelContainerRefChange}
            >
                <StyledWrapper ref={elementStart} />
                <Icon
                    src={ChevronUp}
                    css={mergedStyles.chevronUpIconStyle}
                    size={16}
                    onClick={(e: any) => {
                        handleClick(e, "UP");
                    }}
                    style={{
                        display: isScrollUpVisible ? "inline-block" : "none",
                        cursor: clickable ? "pointer" : "default",
                    }}
                />
                {children}
                <Icon
                    src={ChevronDown}
                    css={mergedStyles.chevronDownIconStyle}
                    size={16}
                    onClick={(e: any) => {
                        handleClick(e, "DOWN");
                    }}
                    style={{
                        display: isScrollDownVisible ? "inline-block" : "none",
                        cursor: clickable ? "pointer" : "default",
                    }}
                />
                <StyledWrapper
                    css={mergedStyles.overRelyStyle}
                    style={{
                        display: isScrollDownVisible && showOverlay ? "inline-block" : "none",
                    }}
                />
                <StyledWrapper ref={elementEnd} />
            </StyledWrapper>
        </>
    );
};

export default FancyScroll;
